/**
 * 协议签署状态管理
 * 使用 Zustand 管理协议签署相关的全局状态
 */

import { create } from "zustand";
import type { Agreement } from "@/types/consent";

/**
 * 签署状态接口
 */
interface ConsentState {
  // 状态
  needsReconsent: boolean;
  pendingAgreements: Agreement[];
  isChecking: boolean;
  isModalOpen: boolean;
  lastCheckedAt: number | null;

  // 操作
  setPendingAgreements: (agreements: Agreement[]) => void;
  setNeedsReconsent: (needs: boolean) => void;
  setIsChecking: (checking: boolean) => void;
  openModal: () => void;
  closeModal: () => void;
  clearPendingAgreements: () => void;
  setLastCheckedAt: (timestamp: number) => void;
  reset: () => void;
}

/**
 * 初始状态
 */
const initialState = {
  needsReconsent: false,
  pendingAgreements: [] as Agreement[],
  isChecking: false,
  isModalOpen: false,
  lastCheckedAt: null as number | null,
};

/**
 * 协议签署状态 Store
 *
 * 功能：
 * - 管理待签署协议列表
 * - 管理签署弹窗显示状态
 * - 缓存签署状态检查时间
 */
export const useConsentStore = create<ConsentState>((set) => ({
  ...initialState,

  setPendingAgreements: (agreements) => set({ pendingAgreements: agreements }),

  setNeedsReconsent: (needs) => set({ needsReconsent: needs }),

  setIsChecking: (checking) => set({ isChecking: checking }),

  openModal: () => set({ isModalOpen: true }),

  closeModal: () => set({ isModalOpen: false }),

  clearPendingAgreements: () =>
    set({
      pendingAgreements: [],
      needsReconsent: false,
    }),

  setLastCheckedAt: (timestamp) => set({ lastCheckedAt: timestamp }),

  reset: () => set(initialState),
}));

// 导出便捷的选择器 hooks
export const useNeedsReconsent = () =>
  useConsentStore((state) => state.needsReconsent);
export const usePendingAgreements = () =>
  useConsentStore((state) => state.pendingAgreements);
export const useIsConsentModalOpen = () =>
  useConsentStore((state) => state.isModalOpen);
export const useHasForceUpdate = () =>
  useConsentStore((state) =>
    state.pendingAgreements.some((a) => a.force_update)
  );

export default useConsentStore;
